import { createContext, useContext, useState, useCallback, useEffect, useRef } from "react";
import api from "../lib/api";
import { useAuth } from "./AuthContext";
import { paramSasaran } from "../hp/sasaranKeranjang";

const CartContext = createContext(null);

export function CartProvider({ children }) {
  const { user } = useAuth();
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(false);
  const [fetched, setFetched] = useState(false);
  // PR #42 perbaikan 2: permintaan keranjang membawa pilihan produk target (paramSasaran). Hanya jawaban permintaan TERAKHIR yang dipakai;
  // muat ulang selagi perubahan (tambah/hapus) belum selesai DITUNDA sampai semua perubahan selesai — GET yang berangkat lebih dulu bisa membaca
  // keranjang sebelum perubahan tersimpan dan menimpa jawaban perubahan. Perubahan yang tumpang-tindih atau gagal → urutan jawaban tidak
  // bisa dipercaya → sesudah semuanya selesai keranjang dimuat ulang sekali dari server (review Codex putaran 1–2).
  const urutan = useRef(0);
  const ubahBerjalan = useRef(0);
  const segarkanNanti = useRef(false);
  const segarkanRef = useRef(null);
  const pakai = (n, data) => { if (n === urutan.current) setSummary(data); return data; };
  const ubahKeranjang = async (kirimPermintaan) => {
    if (ubahBerjalan.current > 0) segarkanNanti.current = true;   // tumpang-tindih
    ubahBerjalan.current += 1;
    const n = ++urutan.current;
    try {
      const { data } = await kirimPermintaan();
      return pakai(n, data);
    } catch (e) {
      segarkanNanti.current = true;   // gagal: jawaban perubahan lain yang sudah dibuang bisa jadi yang benar
      throw e;
    } finally {
      ubahBerjalan.current -= 1;
      if (ubahBerjalan.current === 0 && segarkanNanti.current) { segarkanNanti.current = false; segarkanRef.current?.(); }
    }
  };

  const refresh = useCallback(async () => {
    if (!user || user.role === "admin") {
      setSummary(null);
      setFetched(true);
      return;
    }
    if (ubahBerjalan.current > 0) { segarkanNanti.current = true; return; }
    setLoading(true);
    const n = ++urutan.current;
    try {
      const { data } = await api.get("/api/cart", { params: paramSasaran() });
      pakai(n, data);
    } finally {
      setLoading(false);
      setFetched(true);
    }
  }, [user]);

  segarkanRef.current = refresh;
  useEffect(() => {
    refresh();
  }, [refresh]);

  const addItem = (produk_id, qty_pcs) => ubahKeranjang(() => api.post("/api/cart/items", { produk_id, qty_pcs }, { params: paramSasaran() }));

  // Keranjang HP: "Tambah" ke tingkat diskon berikut untuk produk target — jumlah dihitung server (POST /api/cart/tambah-tingkat)
  const tambahTingkat = (produk_id, kategori) => ubahKeranjang(() => api.post("/api/cart/tambah-tingkat", { produk_id, kategori }, { params: paramSasaran() }));

  const removeItem = async (produk_id) => { await ubahKeranjang(() => api.delete(`/api/cart/items/${produk_id}`, { params: paramSasaran() })); };

  const clear = async () => { await ubahKeranjang(() => api.delete("/api/cart")); };

  const count = summary
    ? summary.categories.reduce((s, c) => s + c.items.length, 0)
    : 0;

  return (
    <CartContext.Provider
      value={{ summary, loading, fetched, refresh, addItem, tambahTingkat, removeItem, clear, count }}
    >
      {children}
    </CartContext.Provider>
  );
}

export const useCart = () => useContext(CartContext);
